'use client';

import React, { useState, useEffect, useRef } from 'react';
import CartReview from './CartReview';
import CheckoutForm, { type FormData } from './CheckoutForm';
import OrderConfirmation from './OrderConfirmation';
import { useCart } from '@/contexts/CartContext';

function generateIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

interface CartItem {
  id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
  alt: string;
  isVeg: boolean;
}

const OrderCheckoutInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState<'cart' | 'checkout' | 'confirmation'>('cart');
  const { cartItems, updateQuantity, removeFromCart, clearCart, orderCityId } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [orderDetails, setOrderDetails] = useState<{
    orderNumber: string;
    estimatedTime: string;
    phoneNumber: string;
    error?: string;
  }>({
    orderNumber: '',
    estimatedTime: '',
    phoneNumber: '',
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleUpdateQuantity = (itemId: string, quantity: number) => {
    updateQuantity(itemId, quantity);
  };

  const handleRemoveItem = (itemId: string) => {
    removeFromCart(itemId);
  };

  const handleCheckoutSubmit = async (formData: FormData) => {
    setIsProcessing(true);
    setOrderDetails((prev) => ({ ...prev, error: undefined }));

    const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const DELIVERY_FEE_THRESHOLD = 200;
    const DELIVERY_FEE = 30;
    const tax = subtotal * 0.05;
    const deliveryFee = subtotal > 0 && subtotal < DELIVERY_FEE_THRESHOLD ? DELIVERY_FEE : 0;
    const total = subtotal + tax + deliveryFee;

    const payload = {
      customerName: formData.name.trim(),
      phone: formData.phone.trim(),
      ...(formData.email?.trim() && { email: formData.email.trim() }),
      deliveryOption: formData.deliveryOption,
      deliveryAddress: formData.deliveryOption === 'delivery' ? formData.address.trim() : undefined,
      deliveryLat: formData.deliveryOption === 'delivery' ? formData.deliveryLat : undefined,
      deliveryLng: formData.deliveryOption === 'delivery' ? formData.deliveryLng : undefined,
      cityId: (orderCityId && orderCityId.trim()) || undefined,
      specialInstructions: formData.specialInstructions?.trim() || undefined,
      paymentMethod: formData.paymentMethod === 'cash' ? 'cash_on_delivery' : 'online',
      items: cartItems.map((item) => ({
        itemId: item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
      })),
      subtotal,
      tax,
      deliveryFee,
      total,
    };

    const isOnlinePayment = formData.paymentMethod === 'online';

    try {
      if (isOnlinePayment) {
        if (!idempotencyKeyRef.current) idempotencyKeyRef.current = generateIdempotencyKey();
        const idemKey = idempotencyKeyRef.current;
        const res = await fetch('/api/phonepe/initiate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Idempotency-Key': idemKey,
          },
          body: JSON.stringify({ ...payload, idempotencyKey: idemKey }),
        });
        const data = await res.json();

        if (!res.ok) {
          setOrderDetails((prev) => ({ ...prev, error: data.error || 'Failed to start payment' }));
          setIsProcessing(false);
          return;
        }

        const redirectUrl = data.redirectUrl || data.tokenUrl;
        if (redirectUrl) {
          clearCart();
          window.location.href = redirectUrl;
          return;
        }
        setOrderDetails((prev) => ({ ...prev, error: 'Payment could not be started. Please try again.' }));
        setIsProcessing(false);
        return;
      }

      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();

      if (!res.ok) {
        setOrderDetails((prev) => ({ ...prev, error: data.error || 'Failed to place order' }));
        setIsProcessing(false);
        return;
      }

      const orderNumber = data.orderNumber ? String(data.orderNumber) : `ORD-${Date.now()}-${Math.floor(1000 + Math.random() * 9000)}`;
      const estimatedTime = formData.deliveryOption === 'delivery' ? '30-45 minutes' : '15-20 minutes';
      setOrderDetails({
        orderNumber,
        estimatedTime,
        phoneNumber: formData.phone,
      });
      setShowConfirmation(true);
      clearCart();
    } catch (_e) {
      setOrderDetails((prev) => ({ ...prev, error: 'Request failed. Please try again.' }));
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  if (!isHydrated) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="h-96 animate-pulse rounded-lg bg-muted"></div>
          <div className="h-96 animate-pulse rounded-lg bg-muted"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {cartItems.length === 0 && !showConfirmation ? (
          <div className="flex min-h-[400px] flex-col items-center justify-center rounded-lg bg-card p-12 text-center shadow-md">
            <div className="text-6xl">🛒</div>
            <h2 className="mt-6 font-heading text-2xl font-semibold text-foreground">
              Your cart is empty
            </h2>
            <p className="mt-2 text-muted-foreground">
              Add some delicious items from our menu to get started
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="order-2 lg:order-1 space-y-4">
              {orderDetails.error && (
                <div className="rounded-lg border border-error/50 bg-error/10 px-4 py-3 text-sm text-error">
                  {orderDetails.error}
                </div>
              )}
              <CheckoutForm onSubmit={handleCheckoutSubmit} isProcessing={isProcessing} />
            </div>

            <div className="order-1 lg:order-2">
              <CartReview
                items={cartItems}
                onUpdateQuantity={handleUpdateQuantity}
                onRemoveItem={handleRemoveItem}
              />
            </div>
          </div>
        )}
      </div>

      {showConfirmation && (
        <OrderConfirmation
          orderNumber={orderDetails.orderNumber}
          estimatedTime={orderDetails.estimatedTime}
          phoneNumber={orderDetails.phoneNumber}
          onClose={handleCloseConfirmation}
        />
      )}
    </>
  );
};

export default OrderCheckoutInteractive;
